/**
 * Money as KollabHR writes it: the Canadian variant of the app language, so
 * English reads "$75,000.50" and French "75 000,50 $" (Quebec: a space
 * between thousands, a comma before the cents). One source for every money
 * input (`MoneyField`) and every money display (`formatMoney`,
 * `formatSalary`), so what a person types looks like what the app shows.
 */

/** The most an amount carries after the decimal mark. */
export const MONEY_MAX_DECIMALS = 2;
/** Digits before the decimal mark (999 999 999 999): beyond any salary. */
const MAX_INTEGER_DIGITS = 12;

/** `fr-CA` for French, `en-CA` for everything else. */
export const moneyLocale = (language?: string | null): string =>
  (language ?? '').toLowerCase().startsWith('fr') ? 'fr-CA' : 'en-CA';

export interface MoneySeparators {
  /** Between thousands: "," in English, a no-break space in French. */
  group: string;
  /** Before the cents: "." in English, "," in French. */
  decimal: string;
}

const separatorCache = new Map<string, MoneySeparators>();

/** The thousands and decimal marks the app shows money with. */
export const moneySeparators = (language?: string | null): MoneySeparators => {
  const locale = moneyLocale(language);
  const cached = separatorCache.get(locale);
  if (cached) return cached;
  const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
  const separators = {
    group: parts.find((part) => part.type === 'group')?.value ?? ',',
    decimal: parts.find((part) => part.type === 'decimal')?.value ?? '.',
  };
  separatorCache.set(locale, separators);
  return separators;
};

const isDigit = (char: string) => char >= '0' && char <= '9';

/**
 * What a person typed, as plain text the API understands: digits with an
 * optional "." ("75000", "24.5", "75." while the cents are still coming).
 * Read in the app language: its decimal mark is the decimal point (in
 * French a "." also counts, as the numeric keypad types one), the thousands
 * marks and spaces are ignored, and anything else is dropped. Two decimals
 * at most; leading zeros go.
 */
export const readTypedMoney = (
  text: string,
  language?: string | null
): string => {
  const { decimal } = moneySeparators(language);
  const decimalMarks = decimal === ',' ? [',', '.'] : [decimal];
  let integer = '';
  let fraction: string | null = null;
  for (const char of text) {
    if (isDigit(char)) {
      if (fraction !== null) {
        if (fraction.length < MONEY_MAX_DECIMALS) fraction += char;
      } else if (integer.length < MAX_INTEGER_DIGITS) {
        integer += char;
      }
    } else if (fraction === null && decimalMarks.includes(char)) {
      fraction = '';
    }
  }
  integer = integer.replace(/^0+(?=\d)/, '');
  return fraction === null ? integer : `${integer}.${fraction}`;
};

/**
 * A pasted amount in whatever format it was copied in ("75,000",
 * "75 000,50", "$75,000.50", "75.000,50 €"), as plain text. A mark
 * followed by exactly three digits separates thousands (an amount never has
 * three decimals); when both "," and "." appear, the last one is the
 * decimal point.
 */
export const readPastedMoney = (text: string): string => {
  const cleaned = text.replace(/[^\d.,]/g, '');
  const lastDot = cleaned.lastIndexOf('.');
  const lastComma = cleaned.lastIndexOf(',');
  let decimalAt = -1;
  if (lastDot >= 0 && lastComma >= 0) {
    decimalAt = Math.max(lastDot, lastComma);
  } else if (lastDot >= 0 || lastComma >= 0) {
    const at = Math.max(lastDot, lastComma);
    const mark = cleaned[at];
    const marks = cleaned.split(mark).length - 1;
    const digitsAfter = cleaned.length - at - 1;
    if (marks === 1 && digitsAfter !== 3) decimalAt = at;
  }
  const integer = (decimalAt >= 0 ? cleaned.slice(0, decimalAt) : cleaned)
    .replace(/\D/g, '')
    .slice(0, MAX_INTEGER_DIGITS)
    .replace(/^0+(?=\d)/, '');
  if (decimalAt < 0) return integer;
  const fraction = cleaned
    .slice(decimalAt + 1)
    .replace(/\D/g, '')
    .slice(0, MONEY_MAX_DECIMALS);
  return `${integer}.${fraction}`;
};

/**
 * Plain text ("75000.5") written the app's way while typing: "75,000.5" in
 * English, "75 000,5" in French. Keeps a trailing decimal mark and the
 * decimals as typed, so the field never fights the person typing.
 */
export const formatTypedMoney = (
  plain: string,
  language?: string | null
): string => {
  if (!plain) return '';
  const { group, decimal } = moneySeparators(language);
  const [integer, fraction] = plain.split('.');
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, group);
  return fraction === undefined ? grouped : `${grouped}${decimal}${fraction}`;
};

/** An amount from the API or a form ("75000.00", 24.5, '') as a number. */
export const toAmount = (
  value: number | string | null | undefined
): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const amount = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(amount) ? amount : null;
};

/** Plain text ("75.", "24.5", '') as the number a form holds; '' when empty. */
export const plainMoneyToValue = (plain: string): number | '' => {
  if (plain === '' || plain === '.') return '';
  const amount = Number(plain);
  return Number.isFinite(amount) ? amount : '';
};

/** What a person typed, as the number sent to the API; null when empty. */
export const parseMoneyInput = (
  text: string,
  language?: string | null
): number | null => {
  const value = plainMoneyToValue(readTypedMoney(text, language));
  return value === '' ? null : value;
};

/**
 * A stored amount as a money field shows it at rest: grouped, no cents on a
 * whole amount, two decimals otherwise ("75,000", "24.50" / "75 000",
 * "24,50"). '' when empty.
 */
export const formatMoneyInput = (
  value: number | string | null | undefined,
  language?: string | null
): string => {
  const amount = toAmount(value);
  if (amount === null) return '';
  const plain = Number.isInteger(amount)
    ? String(amount)
    : amount.toFixed(MONEY_MAX_DECIMALS);
  return formatTypedMoney(plain, language);
};

/**
 * Where the caret belongs once a typed value is reformatted: after as many
 * kept characters (digits and the decimal mark) as were before it, so
 * inserting a thousands separator never makes it jump.
 */
export const caretAfterReformat = (
  typed: string,
  caret: number,
  formatted: string,
  language?: string | null
): number => {
  const keep = readTypedMoney(typed.slice(0, caret), language).length;
  if (keep === 0) return 0;
  const { decimal } = moneySeparators(language);
  let seen = 0;
  for (let index = 0; index < formatted.length; index += 1) {
    const char = formatted[index];
    if (isDigit(char) || char === decimal) seen += 1;
    if (seen === keep) return index + 1;
  }
  return formatted.length;
};

/**
 * An amount with its currency, the app's way: "$75,000" / "75 000 $", with
 * cents only when there are any. Falls back to "75,000 XYZ" for a currency
 * code `Intl` does not know. '' for no amount.
 */
export const formatMoney = (
  value: number | string | null | undefined,
  currency: string | null | undefined,
  language?: string | null
): string => {
  const amount = toAmount(value);
  if (amount === null) return '';
  const locale = moneyLocale(language);
  const fraction = Number.isInteger(amount) ? 0 : MONEY_MAX_DECIMALS;
  const code = currency?.trim().toUpperCase();
  if (code) {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code,
        minimumFractionDigits: fraction,
        maximumFractionDigits: fraction,
      }).format(amount);
    } catch {
      // An unknown code: write the number and the code as they are.
    }
  }
  const number = new Intl.NumberFormat(locale, {
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction,
  }).format(amount);
  return code ? `${number} ${code}` : number;
};

/**
 * A salary with the period its pay basis is expressed in: "$75,000 / month"
 * / "75 000 $ / mois". `period` is the already translated unit ("month",
 * "mois"), from `libsCommon` `money.per.<payType>` (see `useMoneyFormat`);
 * without one only the amount is written.
 */
export const formatSalary = (
  value: number | string | null | undefined,
  currency: string | null | undefined,
  period: string | null | undefined,
  language?: string | null
): string => {
  const money = formatMoney(value, currency, language);
  if (!money || !period) return money;
  // No-break spaces: "/ year" never wraps away from its amount.
  return `${money}\u00a0/\u00a0${period}`;
};

/**
 * The symbol the app writes a currency with: "$" for CAD, "US$" for USD,
 * "€" for EUR. Falls back to the code for a currency `Intl` does not know.
 */
export const currencySymbol = (
  currency: string | null | undefined,
  language?: string | null
): string => {
  const code = currency?.trim().toUpperCase();
  if (!code) return '';
  try {
    return (
      new Intl.NumberFormat(moneyLocale(language), {
        style: 'currency',
        currency: code,
      })
        .formatToParts(0)
        .find((part) => part.type === 'currency')?.value ?? code
    );
  } catch {
    return code;
  }
};

/**
 * The unit a pay amount is typed in, for the end of a money input:
 * "$ / h", "$ / mois", "US$ / yr". `period` is the already translated
 * short unit (`libsCommon` `money.per_short.<payType>`); without one only
 * the symbol is written.
 */
export const formatMoneyUnit = (
  currency: string | null | undefined,
  period: string | null | undefined,
  language?: string | null
): string => {
  const symbol = currencySymbol(currency, language);
  if (!period) return symbol;
  return symbol ? `${symbol}\u00a0/\u00a0${period}` : period;
};
